import { getAuth } from 'firebase/auth';
import Logger from '@/utils/logger';

const isDevelopment = import.meta.env.DEV;
const baseURL = isDevelopment ? '/api' : String(import.meta.env.VITE_API_URL);

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const user = getAuth().currentUser;
  if (!user) {
    return {};
  }
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const method = init?.method?.toUpperCase() || 'GET';
  const url = `${baseURL}${path}`;

  const response = await fetch(url, {
    ...init,
    headers: {
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...authHeaders,
      ...init?.headers,
    },
  });

  if (!response.ok) {
    const statusText = `${response.status} ${response.statusText}`;

    // 401 é tratado na UI como "email sem loja vinculada", não é um erro inesperado
    if (response.status !== 401) {
      Logger.apiError(statusText, {
        url,
        method,
        statusCode: response.status,
        errorType: response.status >= 500 ? 'server_error' : 'client_error',
        requestId: response.headers.get('x-request-id') ?? undefined,
      });
    }

    throw new ApiError(response.status, statusText);
  }

  return response.json() as Promise<T>;
}
